import styled from "@emotion/styled";
import { groupBy, mapValues } from "lodash";
import { ChangeEvent, FC, FormEvent, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Info as InfoIcon } from "@livechat/design-system-icons";
import {
  Alert,
  Button,
  Heading,
  IPickerListItem,
  Icon,
  Info,
  Input,
  Modal,
  ModalPortal,
  Picker,
  SpacingToken,
  Text,
  Tooltip,
} from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { TeamPickerListItem, TeamPickerSelectedListItem } from "components/picker-items/teamPickerItems";
import { dateSort } from "helpers/sort";
import { EmailDomainStatus, IEmailDomain, selectActiveDomainsMap } from "store/entities/email-domains";
import { selectLicenseId } from "store/entities/license";
import { ReplyAddressActions } from "store/entities/reply-addresses/actions";
import {
  IWithReplyAddressesState,
  selectIsCreatingReplyAddress,
  selectIsUpdatingReplyAddress,
  selectReplyAddress,
} from "store/entities/reply-addresses/selectors";
import { selectTeamsAlphabetically } from "store/entities/teams";
import { fullWidthInput } from "styles/common";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

import { FormFieldNoMargin } from "../../FormFieldNoMargin";

import ReplyAddressPicker from "./ReplyAddressPicker";
import { displayNameValidation, replyAddressValidation, teamCountValidation } from "./validation";

interface IFooterProps {
  onClose(): void;
  isEditing?: boolean;
}

const AddressSettingsModalFooter: FC<IFooterProps> = ({ onClose, isEditing }) => {
  const isEditingLoading = useSelector(selectIsUpdatingReplyAddress);
  const isCreatingLoading = useSelector(selectIsCreatingReplyAddress);

  const isLoading = isEditing ? isEditingLoading : isCreatingLoading;

  return (
    <Flex gap={withVar(SpacingToken.Spacing2)}>
      <Button size="medium" kind="secondary" type="button" onClick={onClose}>
        Cancel
      </Button>
      <Button size="medium" kind="primary" type="submit" form="reply-address-form" loading={isLoading}>
        {isEditing ? "Update" : "Add"}
      </Button>
    </Flex>
  );
};

interface IProps {
  fromName: string | null;
  id?: string;
  onClose(): void;
  isEdit?: boolean;
}

interface IReplyAddressValue {
  prefix: string;
  emailDomainID: string | null;
}

interface IReplyAddressValueEmailDomain extends IReplyAddressValue {
  emailDomainID: string;
}

const AddressSettingsModal: FC<IProps> = ({ fromName, id, onClose, isEdit: isEditing = false }) => {
  const dispatch = useDispatch();

  const licenseId = useSelector(selectLicenseId);
  const replyAddress = useSelector(state => selectReplyAddress(state as IWithReplyAddressesState, id || ""));
  const domains: Record<string, IEmailDomain> = useSelector(selectActiveDomainsMap);
  const teams = useSelector(selectTeamsAlphabetically);

  const isUpdating = useSelector(selectIsUpdatingReplyAddress);
  const [prevIsUpdating, setPrevIsUpdating] = useState(isUpdating);

  const isCreating = useSelector(selectIsCreatingReplyAddress);
  const [prevIsCreating, setPrevIsCreating] = useState(isCreating);

  const bestDomain = useMemo(() => {
    const { helpdesk = [], custom = [] } = mapValues(
      groupBy(Object.values(domains), domain => (domain.name.endsWith(".helpdesk.com") ? "helpdesk" : "custom")),
      grouped => grouped.sort((d, d2) => dateSort(d.updatedAt || d.createdAt, d2.updatedAt || d2.createdAt))
    );

    return (custom.length > 0 ? custom[0] : helpdesk[0]) || null;
  }, [domains]);

  const bestDomainID = bestDomain ? bestDomain.id : null;

  const currentAssignedTeamIDs = isEditing
    ? teams // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter((team: any) => team.replyAddressID === (id || null) && team.replyName === fromName) // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((t: any) => t.id)
    : [];

  const [displayName, setDisplayName] = useState(fromName ?? "");

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const teamPickerOptions = teams.map((team: any) => ({
    key: team.id,
    name: team.name,

    customElement: {
      listItemBody: <TeamPickerListItem name={team.name} pickerType="multi" />,
      selectedItemBody: <TeamPickerSelectedListItem name={team.name} pickerType="single" />,
    },
  }));

  const [selectedTeams, setSelectedTeams] = useState<IPickerListItem[]>(
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    teamPickerOptions.filter((team: any) => currentAssignedTeamIDs.includes(team.key))
  );
  const assignedTeamIDs = selectedTeams.map(team => team.key);

  const [nameError, setNameError] = useState("");
  const [replyAddressError, setReplyAddressError] = useState("");
  const [teamCountError, setTeamCountError] = useState("");

  const [{ prefix, emailDomainID }, setReplyAddress] = useState<IReplyAddressValue>({
    prefix: replyAddress?.prefix || "",
    emailDomainID: replyAddress?.emailDomainID || (!isEditing && bestDomainID) || null,
  });

  const isDomainVerified =
    emailDomainID === null || [EmailDomainStatus.Active, EmailDomainStatus.New].includes(domains[emailDomainID].status);

  const isValid =
    !displayNameValidation(displayName) &&
    (isEditing || !replyAddressValidation(prefix, emailDomainID)) &&
    !teamCountValidation(assignedTeamIDs);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const createReplyAddress = (value: IReplyAddressValueEmailDomain | null, name: string, teamIDs: string[]) => {
    dispatch(ReplyAddressActions.create({ value, displayName: name, teamIDs }));
  };

  // FIXME: Fix this the next time the file is edited.
  const showValidationErrors = (): void => {
    setNameError(displayNameValidation(displayName) || "");
    setReplyAddressError(replyAddressValidation(prefix, emailDomainID) || "");
    setTeamCountError(teamCountValidation(assignedTeamIDs) || "");
  };

  const updateReplyAddress = (
    updatedID: string | null,
    oldDisplayName: string | null,
    name: string,
    teamIDs: string[]
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  ) => {
    dispatch(
      ReplyAddressActions.update({
        id: updatedID,
        displayName: name,
        oldDisplayName,
        teamIDs,
      })
    );
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleSave = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    showValidationErrors();
    if (isValid) {
      if (isEditing) {
        updateReplyAddress(id ?? null, fromName, displayName, assignedTeamIDs);
      } else {
        createReplyAddress(
          emailDomainID === null ? null : { prefix: prefix.toLowerCase(), emailDomainID },
          displayName,
          assignedTeamIDs
        );
      }
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleDisplayNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDisplayName(event.target.value);
    setNameError("");
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleDisplayNameBlur = () => {
    setNameError(displayNameValidation(displayName ?? "") || "");
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleReplyAddressValidation = () => setReplyAddressError(replyAddressValidation(prefix, emailDomainID) || "");

  useEffect(() => {
    if ((prevIsCreating && !isCreating) || (prevIsUpdating && !isUpdating)) {
      onClose();
    }

    setPrevIsCreating(isCreating);
    setPrevIsUpdating(isUpdating);
  }, [isCreating, isUpdating, prevIsCreating, prevIsUpdating]);

  return (
    <ModalPortal zIndex={StackingContextLevel.Modal}>
      <SizedModal
        onClose={onClose}
        heading={isEditing ? "Edit reply address" : "Add reply address"}
        footer={<AddressSettingsModalFooter onClose={onClose} isEditing={isEditing} />}
      >
        <form onSubmit={handleSave} id="reply-address-form">
          <Flex column gap={withVar(SpacingToken.Spacing8)}>
            <Flex column gap={withVar(SpacingToken.Spacing4)}>
              {id && isEditing && !replyAddress?.verified && (
                <Alert kind="warning">
                  The <strong>email address isn&apos;t verified</strong>. Ensure you&apos;ve set up forwarding from the
                  entered email address to the HelpDesk&apos;s forwarding address.
                </Alert>
              )}
              {/* @ts-expect-error TS(2322) */}
              <FormFieldNoMargin labelText={<DisplayNameInfo />} error={nameError}>
                <Input
                  id="display-name"
                  value={displayName}
                  placeholder="Set a display name, e.g., company name"
                  onBlur={handleDisplayNameBlur}
                  onChange={handleDisplayNameChange}
                  css={[fullWidthInput]}
                  error={!!nameError}
                />
              </FormFieldNoMargin>
              <ReplyAddressPicker
                replyAddress={{ prefix, emailDomainID }}
                domains={domains}
                licenseId={licenseId}
                disabled={isEditing}
                error={replyAddressError}
                setReplyAddress={setReplyAddress}
                validate={handleReplyAddressValidation}
              />
              {isDomainVerified && emailDomainID !== null && !isEditing && (
                <Alert>
                  Before adding this reply address, <strong>ensure you&apos;ve set up forwarding</strong> from the
                  entered email address to the HelpDesk&apos;s forwarding address.
                </Alert>
              )}
            </Flex>
            <Flex column gap={withVar(SpacingToken.Spacing4)} maxWidth="100%">
              <Heading size="xs">Teams using this reply address to communicate:</Heading>
              <FormFieldNoMargin error={teamCountError}>
                <Picker
                  data-testid="team-picker"
                  type="multi"
                  options={teamPickerOptions}
                  selected={selectedTeams}
                  error={!!teamCountError}
                  // FIXME: Fix this the next time the file is edited.
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  onSelect={(selected: any) => {
                    if (!selected) {
                      return setSelectedTeams([]);
                    }
                    setSelectedTeams(selected);
                  }}
                  onClose={() => setTeamCountError(teamCountValidation(assignedTeamIDs))}
                  hideClearButton
                  placeholder="Select teams"
                />
              </FormFieldNoMargin>
            </Flex>
          </Flex>
        </form>
      </SizedModal>
    </ModalPortal>
  );
};

const SizedModal = styled(Modal)`
  width: 100%;
  max-width: 560px;
`;

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const DisplayNameInfo = () => {
  return (
    <Flex gap={withVar(SpacingToken.Spacing2)}>
      <Text size="sm">Display name</Text>
      <Tooltip kind="invert" placement="top-start" triggerRenderer={<Icon source={InfoIcon} size="small" />}>
        <Info
          theme="invert"
          text="The display name is what the recipients see as the sender's name in their inbox, not just the email address. Personalize it so it's clear who the message is from."
        />
      </Tooltip>
    </Flex>
  );
};
export default AddressSettingsModal;
